import { z } from 'zod'

import { routesDuSite } from './siteRoutes.js'

// Un montant est un entier de centimes : 23,33 € vaut 2333. Aucun flottant,
// pour que les remises calculées du lot 3 tombent juste.
export const centsSchema = z
  .number()
  .int('Le montant doit être un nombre entier de centimes')
  .nonnegative('Le montant ne peut pas être négatif')

// `null` signifie « non proposé » — état distinct d'un prix à zéro, qui
// désignerait une prestation offerte. Remplace les mentions « Indisponible ».
export const optionalCentsSchema = centsSchema.nullable()

export const uniteSchema = z.enum(['personne', 'seance'], {
  errorMap: () => ({ message: "L'unité doit être « personne » ou « séance »" }),
})
export const uniteUniqueSchema = z.enum(['total', 'personne', 'seance'], {
  errorMap: () => ({ message: "L'unité doit être « total », « personne » ou « séance »" }),
})

const passAmounts = z.object({
  type: z.literal('pass'),
  avecPass: optionalCentsSchema,
  sansPass: optionalCentsSchema,
})

const duoAmounts = z.object({
  type: z.literal('duo'),
  seuleAvecPass: optionalCentsSchema,
  seuleSansPass: optionalCentsSchema,
  aDeux: optionalCentsSchema,
})

// Le prix unitaire n'est PAS stocké : il vaut total ÷ quantité, et les 24
// lignes concernées du catalogue vérifient cette division au centime près.
// Le stocker rouvrirait la divergence que ce lot ferme.
const quantiteAmounts = z.object({
  type: z.literal('quantite'),
  unite: uniteSchema,
  quantite: z.number().int('La quantité doit être un nombre entier').min(1, 'La quantité doit valoir au moins 1'),
  total: centsSchema,
})

const forfaitAmounts = z.object({
  type: z.literal('forfait'),
  prixSeance: centsSchema,
  nombreSeances: z.number().int('Le nombre de séances doit être un nombre entier').min(2, 'Un forfait compte au moins deux séances'),
  prixForfait: centsSchema,
})

const uniqueAmounts = z.object({
  type: z.literal('unique'),
  prix: centsSchema,
  unite: uniteUniqueSchema,
})

// `.superRefine` est posé sur l'union et non sur ses membres : Zod refuse
// qu'un membre de discriminatedUnion soit un ZodEffects, ce que produirait un
// `.refine()` posé sur l'un des objets ci-dessus.
const amountsDiscriminated = z.discriminatedUnion('type', [
  passAmounts,
  duoAmounts,
  quantiteAmounts,
  forfaitAmounts,
  uniqueAmounts,
])

export const amountsSchema = z
  .object({ type: z.string() })
  .passthrough()
  .superRefine((data, ctx) => {
    const validTypes = ['pass', 'duo', 'quantite', 'forfait', 'unique'] as const
    if (typeof data.type === 'string' && !validTypes.includes(data.type as never)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['type'],
        message: 'Le type de tarif est invalide',
      })
      return
    }
  })
  .pipe(
    amountsDiscriminated.superRefine((amounts, ctx) => {
      if (amounts.type === 'pass' && amounts.avecPass === null && amounts.sansPass === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sansPass'],
          message: 'Renseignez au moins un des deux tarifs',
        })
      }
      // La règle de « duo » garantit un tarif « seule » ; contrairement à « pass »,
      // elle ne requiert pas les deux. « aDeux » reste facultatif car deux
      // prestations du catalogue ne sont pas proposées en duo (pas de « Soin 4 mains »
      // dans certaines situations). L'affichage s'appuie sur cette asymétrie.
      if (amounts.type === 'duo' && amounts.seuleAvecPass === null && amounts.seuleSansPass === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['seuleSansPass'],
          message: 'Renseignez au moins un tarif pour une personne seule',
        })
      }
      // Un « forfait » dont le prix global n'est pas inférieur au produit n'est
      // pas remisé : c'est une cure au prorata, donc le type « quantite ». Sans
      // cette garde, les deux types deviennent interchangeables à la saisie et
      // l'affichage annonce une économie nulle ou négative.
      if (amounts.type === 'forfait' && amounts.prixForfait >= amounts.prixSeance * amounts.nombreSeances) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['prixForfait'],
          message:
            'Le prix du forfait doit être inférieur au prix à la séance multiplié par le nombre de séances. ' +
            'Sinon, choisissez « Cure de plusieurs séances ».',
        })
      }
    })
  )

export type Amounts = z.infer<typeof amountsSchema>
export type PricingType = Amounts['type']

const nomObligatoire = (quoi: string) => z.string().trim().min(1, `Le nom ${quoi} est obligatoire`)

/**
 * Vocabulaire de rattrapage : les mots que la visiteuse tape, quand ce ne sont
 * pas ceux du catalogue. Le catalogue dit « modelage », « lumière pulsée »,
 * « Beauté des mains » ; elle cherche « massage », « laser », « manucure ».
 * Rien ne faisait le pont, et ces trois recherches ne rendaient rien (#97).
 *
 * Le champ existe aux trois niveaux parce que le bon niveau dépend du terme :
 * « minceur » qualifie une famille entière, « laser » les seuls groupes de
 * lumière pulsée, « hawaïen » la seule prestation lomi-lomi. Tout poser à la
 * racine ferait remonter les gommages sur « massage » — c'est-à-dire échanger
 * un silence contre du bruit.
 *
 * Ces mots ne sont jamais affichés : ils n'entrent que dans l'index de
 * recherche (voir catalogSearch.ts). Une faute d'orthographe y est donc sans
 * conséquence visible, ce qui est précisément l'intérêt d'un champ que la
 * gérante remplit librement.
 *
 * `.default(() => [])` plutôt qu'un champ obligatoire : tout ce qui a été
 * écrit avant lui — copie de secours embarquée, lignes en base, brouillon
 * posté par une administration pas encore rechargée — doit continuer à
 * valider. La valeur par défaut est produite par une FONCTION, sinon Zod rend
 * le même tableau à chaque analyse et deux prestations partageraient la même
 * instance. Le type déduit reste `string[]` : aucun appelant n'a à traiter
 * l'absence.
 */
const keywordsSchema = z
  .array(z.string().trim().min(1, 'Un mot-clé ne peut pas être vide'))
  .default(() => [])

const serviceBase = z.object({
  // Facultatif pour le seul type « quantite », où il se compose de la
  // quantité et de l'unité (voir computedServiceLabel). La règle croisée est
  // dans le superRefine ci-dessous.
  name: z.string().trim().min(1, 'Le nom de la prestation ne peut pas être vide').nullable(),
  duration: z.string().trim().min(1, 'La durée ne peut pas être vide').nullable(),
  amounts: amountsSchema,
  isExtra: z.boolean(),
  visible: z.boolean(),
  keywords: keywordsSchema,
})

function reglesPrestation(service: z.infer<typeof serviceBase>, ctx: z.RefinementCtx) {
  if (service.name === null && service.amounts.type !== 'quantite') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['name'],
      message: 'Le nom de la prestation est obligatoire',
    })
  }
}

const groupBase = z.object({
  name: nomObligatoire('du groupe'),
  visible: z.boolean(),
  keywords: keywordsSchema,
})

/**
 * Le lien facultatif d'un encart : un libellé, et une page du site.
 *
 * La destination est une énumération fermée et non une adresse libre, pour la
 * même raison que `bannerTargetSchema` : une adresse tapée à la main est un
 * lien mort dès le premier renommage, et personne ne le rouvre jamais pour
 * s'en apercevoir. `routesDuSite` est déjà la liste unique des adresses que le
 * site sait rendre — le routeur, l'API et le plan de site la lisent.
 *
 * C'est aussi ce qui ferme la question du schéma d'URL : aucune adresse
 * externe n'entre ici, donc aucun `javascript:` ne peut atteindre un `href`.
 *
 * Corollaire à connaître : retirer une entrée de `routesDuSite` invaliderait
 * les encarts qui la désignent, à la lecture comme à l'écriture. Une route
 * qui disparaît demande donc une migration, pas seulement une suppression.
 */
const lienEncartSchema = z.object({
  libelle: z.string().trim().min(1, 'Le libellé du lien ne peut pas être vide'),
  // L'errorMap n'est pas décoratif : sans lui, Zod rend « Invalid enum value.
  // Expected '/' | '/accueil' | … » et « Required », en anglais et avec la
  // liste des chemins techniques. Ce message-là traverse l'API jusqu'à la
  // barre d'enregistrement du back-office, où c'est la gérante qui le lit.
  // Même patron que `uniteSchema` plus haut.
  destination: z.enum(routesDuSite, {
    errorMap: () => ({ message: 'Cette page du site est inconnue' }),
  }),
})

/**
 * L'encart d'information d'un univers de soins : ce que la visiteuse doit
 * avoir lu avant de partir réserver sur Planity (issue #164).
 *
 * Il porte les règles d'un espace privatif, une contre-indication, une
 * préparation à demander — tout ce qui, non lu, coûte une séance à
 * interrompre ou un créneau perdu. La réservation partant vers un tunnel que
 * nous ne maîtrisons pas, la page Services est la seule fenêtre où cette
 * information peut être lue.
 *
 * `.nullable().default(null)`, dans cet ordre, et pas l'inverse : `.default()`
 * ne se déclenche que sur `undefined`. L'absence du champ — copie de secours
 * embarquée, lignes déjà en base, brouillon posté par une administration pas
 * encore rechargée — devient `null` ; un `null` écrit, lui, traverse tel quel.
 * Même intention que `keywordsSchema`, forme différente : l'absence d'encart
 * est un état à part entière, là où l'absence de mots-clés est un tableau
 * vide.
 *
 * Le plafond du corps protège la grille du panneau de tarifs : mille
 * caractères, c'est l'essentiel, celui qui doit être lu sans clic. Ce qui
 * déborde part derrière le lien, qui n'existe que pour ça.
 *
 * Ce texte n'entre pas dans l'index de recherche (voir catalogSearch.ts) : un
 * règlement n'est pas un critère de recherche de prestation, et indexer ses
 * mots ferait remonter l'univers sur eux.
 */
/**
 * Ce que le texte de l'encart *est* — pas une couleur, pas une gravité.
 *
 * Deux traitements plutôt qu'un, tranché par la maquette : les deux textes
 * réels ne sont pas deux tons d'une même chose, ce sont deux contrats de
 * lecture. Le règlement du spa privatif sera opposé à la cliente ; ne pas
 * l'avoir lu lui coûte une séance interrompue et 100 € de frais. La
 * contre-indication de la lumière pulsée l'aide ; ne pas l'avoir lue lui coûte
 * un créneau. Servis à l'identique, le premier perd son autorité et le second
 * en prend une qu'il n'a pas.
 *
 * `conseil` est le défaut parce que l'accident le plus coûteux est la fausse
 * alerte : un conseil pratique servi en surface sombre refroidit une cliente
 * qui allait réserver. Poser une règle du lieu est un acte délibéré.
 */
const traitementEncartSchema = z
  .enum(['conseil', 'regle'], {
    errorMap: () => ({ message: "Le traitement de l'encart est invalide" }),
  })
  .default('conseil')

const encartSchema = z
  .object({
    titre: z.string().trim().min(1, "Le titre de l'encart ne peut pas être vide"),
    traitement: traitementEncartSchema,
    corps: z
      .string()
      .trim()
      .min(1, "Le texte de l'encart ne peut pas être vide")
      .max(1000, "Le texte de l'encart ne peut pas dépasser 1000 caractères"),
    lien: lienEncartSchema.nullable().default(null),
  })
  .nullable()
  .default(null)

const familyBase = z.object({
  name: nomObligatoire('de la famille'),
  icon: z.string().trim().min(1, "L'icône est obligatoire"),
  tagline: z.string().trim().min(1, "L'accroche est obligatoire"),
  visible: z.boolean(),
  keywords: keywordsSchema,
  // Sur la famille seule : c'est le niveau du besoin, et le seul qui ait un
  // en-tête de panneau où poser l'encart. Un champ par prestation produirait
  // un mur de pavés.
  encart: encartSchema,
})

export const idSchema = z.string().uuid('Identifiant invalide')

export const serviceSchema = serviceBase.extend({ id: idSchema }).superRefine(reglesPrestation)

export const serviceGroupSchema = groupBase.extend({
  id: idSchema,
  services: z.array(serviceSchema),
})

export const familySchema = familyBase.extend({
  id: idSchema,
  groups: z.array(serviceGroupSchema),
})

// L'ordre est porté par la position dans ces tableaux : aucun champ
// `position` n'apparaît dans les contrats. L'API le renumérote à l'écriture.
export const catalogSchema = z.array(familySchema)

// Brouillons : ce que l'administration envoie. Un objet sans identifiant est
// une création ; un identifiant existant absent de la charge utile est une
// suppression (voir saveFamily).
export const serviceDraftSchema = serviceBase
  .extend({ id: idSchema.optional() })
  .superRefine(reglesPrestation)

export const serviceGroupDraftSchema = groupBase.extend({
  id: idSchema.optional(),
  services: z.array(serviceDraftSchema),
})

export const familyDraftSchema = familyBase.extend({
  id: idSchema.optional(),
  groups: z.array(serviceGroupDraftSchema),
})

export const familyOrderSchema = z.object({
  ids: z.array(idSchema).min(1, 'La liste des familles ne peut pas être vide'),
})

export type Service = z.infer<typeof serviceSchema>
export type ServiceGroup = z.infer<typeof serviceGroupSchema>
export type TraitementEncart = z.infer<typeof traitementEncartSchema>
export type Encart = z.infer<typeof encartSchema>
export type Family = z.infer<typeof familySchema>
export type Catalog = z.infer<typeof catalogSchema>
export type ServiceDraft = z.infer<typeof serviceDraftSchema>
export type ServiceGroupDraft = z.infer<typeof serviceGroupDraftSchema>
export type FamilyDraft = z.infer<typeof familyDraftSchema>
export type FamilyOrder = z.infer<typeof familyOrderSchema>
